/**
 * AttitudeModel — a small top-down drone that mimics the aircraft's orientation
 * through playback, sat between the two control sticks.
 *
 * Slaved to the profile's playhead like the strips: setIndex moves it, it never
 * drives seeking. Yaw rotates it against the north-up map; pitch and roll tilt
 * it in 3D. The props spin while the motors are on and stop at touchdown — the
 * final motor-off frame the parser recovers is exactly where they cut.
 *
 * The sign of each axis and the yaw zero were validated against a real flight in
 * the calibration harness. Each is a single constant, so correcting one — if a
 * future firmware ever flips a convention — is a one-character edit.
 */

const YAW_SIGN = 1;
const PITCH_SIGN = -1;
const ROLL_SIGN = 1;
const YAW_OFFSET = 0;    // degrees
const TILT_GAIN = 1.5;   // pitch/roll exaggeration for legibility
const MAX_TILT = 70;     // clamp, so a stray reading cannot invert the model

export class AttitudeModel {
  constructor(container, flight) {
    this.container = container;
    this.flight = flight;
    this.index = 0;
    this.render();
  }

  available() {
    return this.flight.has('pitchDeg') || this.flight.has('rollDeg') || this.flight.has('yawDeg');
  }

  render() {
    if (!this.available()) {
      this.container.innerHTML = '';
      this.drone = null;
      return;
    }

    this.container.innerHTML = `
      <div class="attitude-well">
        <div class="attitude-persp">
          <div class="attitude-drone" id="attitude-drone" role="img" aria-label="Aircraft orientation">
            ${droneSvg()}
          </div>
        </div>
      </div>
      <div class="gimbal-caption">orientation</div>`;

    this.drone = this.container.querySelector('#attitude-drone');
    this._displayYaw = null;
    this.setIndex(this.index);
  }

  /** Move the model to a row. Called by the app whenever the index changes. */
  setIndex(index) {
    this.index = index;
    if (!this.drone) return;

    const cols = this.flight.cols;
    const yaw = value(cols.yawDeg, index);
    const pitch = value(cols.pitchDeg, index);
    const roll = value(cols.rollDeg, index);

    // Yaw wraps at the +/-180 seam, so 179 -> -179 is a 2 degree turn reported
    // as a 358 degree jump. Left as-is the CSS transition animates that jump the
    // long way round — a full spin every time the heading crosses the seam.
    // Accumulate a continuous heading instead: add only the shortest signed step
    // each frame, so consecutive values never differ by more than 180 and the
    // transition always takes the short way, wherever the seam falls.
    const target = YAW_SIGN * yaw + YAW_OFFSET;
    if (this._displayYaw == null) {
      this._displayYaw = target;
    } else {
      const step = ((target - this._displayYaw + 180) % 360 + 360) % 360 - 180;
      this._displayYaw += step;
    }

    const rz = this._displayYaw;
    const rx = clampTilt(PITCH_SIGN * pitch * TILT_GAIN);
    const ry = clampTilt(ROLL_SIGN * roll * TILT_GAIN);

    this.drone.style.transform =
      `rotateZ(${rz.toFixed(1)}deg) rotateX(${rx.toFixed(1)}deg) rotateY(${ry.toFixed(1)}deg)`;

    const on = cols.motorsOn ? cols.motorsOn[index] === true : false;
    this.drone.classList.toggle('spinning', on);
  }

  destroy() {
    this.container.innerHTML = '';
  }
}

function value(col, i) {
  const v = col ? col[i] : null;
  return v === null || v === undefined ? 0 : v;
}

function clampTilt(v) {
  return Math.max(-MAX_TILT, Math.min(MAX_TILT, v));
}

/**
 * Top-down quad. Front two props carry the accent, rear two are muted, and a
 * nose marker sits at the front, so heading reads even before the model turns.
 * Colours come from the viewer's tokens so it sits in the same palette.
 */
export function droneSvg() {
  return `
    <svg viewBox="-60 -60 120 120" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <g stroke="var(--rule-bright)" stroke-width="7" stroke-linecap="round">
        <line x1="0" y1="0" x2="-38" y2="-38"/><line x1="0" y1="0" x2="38" y2="-38"/>
        <line x1="0" y1="0" x2="-38" y2="38"/><line x1="0" y1="0" x2="38" y2="38"/>
      </g>
      <g>
        <circle cx="-38" cy="-38" r="17" fill="var(--muted)" fill-opacity="0.14" stroke="var(--muted)" stroke-width="1"/>
        <circle cx="38" cy="-38" r="17" fill="var(--muted)" fill-opacity="0.14" stroke="var(--muted)" stroke-width="1"/>
        <circle cx="-38" cy="38" r="17" fill="var(--muted)" fill-opacity="0.14" stroke="var(--muted)" stroke-width="1"/>
        <circle cx="38" cy="38" r="17" fill="var(--muted)" fill-opacity="0.14" stroke="var(--muted)" stroke-width="1"/>
      </g>
      <g stroke-width="3" stroke-linecap="round" stroke="var(--muted)">
        <g class="attitude-prop"><line x1="-51" y1="-38" x2="-25" y2="-38"/><line x1="-38" y1="-51" x2="-38" y2="-25"/></g>
        <g class="attitude-prop"><line x1="25" y1="-38" x2="51" y2="-38"/><line x1="38" y1="-51" x2="38" y2="-25"/></g>
        <g class="attitude-prop"><line x1="-51" y1="38" x2="-25" y2="38"/><line x1="-38" y1="25" x2="-38" y2="51"/></g>
        <g class="attitude-prop"><line x1="25" y1="38" x2="51" y2="38"/><line x1="38" y1="25" x2="38" y2="51"/></g>
      </g>
      <g stroke="var(--ink)" stroke-width="1">
        <circle cx="-38" cy="-38" r="6" fill="var(--ok)"/>
        <circle cx="38" cy="-38" r="6" fill="var(--ok)"/>
        <circle cx="-38" cy="38" r="6" fill="var(--warn)"/>
        <circle cx="38" cy="38" r="6" fill="var(--warn)"/>
      </g>
      <rect x="-13" y="-17" width="26" height="36" rx="8" fill="var(--panel)" stroke="var(--rule-bright)" stroke-width="1.5"/>
      <circle cx="0" cy="-15" r="4.5" fill="var(--ink)" stroke="var(--rule-bright)" stroke-width="1"/>
      <path d="M 0 -34 L -6 -24 L 6 -24 Z" fill="var(--accent)"/>
    </svg>`;
}
